import { useMemo } from 'react';
import type {
  PositionBudgetSummary,
  ScholarshipModel,
  ScholarshipPlayer,
} from '../../../../types/scholarships';
import { PitchingStaffRow } from './PitchingStaffRow';
import type { CardAppearance } from './depthChartPositions';
import { formatGroupToggleAggregate, type DepthChartDisplayMode } from './scholarshipPercent';
import { PositionBudgetLine } from './PositionBudgetLine';

interface Props {
  label: 'RHP' | 'LHP';
  appearances: CardAppearance[];
  scholarshipModel: ScholarshipModel;
  displayMode: DepthChartDisplayMode;
  positionBudget?: PositionBudgetSummary;
  onClickPlayer: (player: ScholarshipPlayer) => void;
}

export const PitchingStaffCard = ({
  label,
  appearances,
  scholarshipModel,
  displayMode,
  positionBudget,
  onClickPlayer,
}: Props) => {
  // Count + aggregate use PRIMARY appearances only (secondary entries are visual-only).
  const primaryPlayers = useMemo(
    () => appearances.filter((a) => a.appearance === 'primary').map((a) => a.player),
    [appearances],
  );
  const avg = useMemo(
    () => formatGroupToggleAggregate(primaryPlayers, scholarshipModel, displayMode),
    [primaryPlayers, scholarshipModel, displayMode],
  );

  return (
    <section className="depth-chart-staff-card" aria-labelledby={`staff-${label}-heading`}>
      <header className="depth-chart-staff-card__header">
        <h3 id={`staff-${label}-heading`} className="depth-chart-staff-card__label">
          {label}
        </h3>
        <span className="depth-chart-staff-card__count">({primaryPlayers.length})</span>
        <span
          className={`depth-chart-staff-card__avg${avg.isWalkOn ? ' depth-chart-staff-card__avg--walkon' : ''}`}
          title={avg.isWalkOn ? 'Group average — all pitchers are walk-ons or have $0 awarded' : undefined}
        >
          {avg.label} {avg.text}
        </span>
      </header>

      {positionBudget && (
        <PositionBudgetLine budget={positionBudget} scholarshipModel={scholarshipModel} />
      )}

      {appearances.length === 0 ? (
        <div className="depth-chart-staff-card__empty">No pitchers</div>
      ) : (
        <ol className="depth-chart-staff-card__list">
          {appearances.map((a, idx) => (
            <PitchingStaffRow
              key={`${a.player.id}-${a.appearance}`}
              player={a.player}
              depthIndex={idx}
              appearance={a.appearance}
              cardLabel={label}
              scholarshipModel={scholarshipModel}
              onClick={() => onClickPlayer(a.player)}
            />
          ))}
        </ol>
      )}
    </section>
  );
};
